import { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useRecoilState } from "recoil";
import { toast } from "react-toastify";
import { t } from "i18next";
import {
  projectImagesInfoState,
  selectedImageState,
  projectCountInfoState,
  multiSelectedSeqsState,
} from "../../recoil";
import { editImageLabel } from "../../services/projectService";

interface LabelButtonProps {
  class: string;
  configNumber: string;
  registerClick?: (fn: (() => void) | null) => void;
}

export default function LabelButton({ class: className, configNumber, registerClick }: LabelButtonProps) {
  const { id } = useParams();
  const [projectImagesInfo, setProjectImagesInfo] = useRecoilState(projectImagesInfoState);
  const [selectedRow, setSelectedRow] = useRecoilState(selectedImageState);
  const [projectCountInfo, setProjectCountInfo] = useRecoilState(projectCountInfoState);
  const [multiSeqs, setMultiSeqs] = useRecoilState(multiSelectedSeqsState);

  // ImageTable에서 Shift/Ctrl로 다중 선택된 행 전체에 라벨 적용
  const handleBulkLabel = async (newLabel: string) => {
    const seqs = new Set(multiSeqs);
    const targets = projectImagesInfo.filter((r) => seqs.has(r.seq!) && r.label !== newLabel);
    const done: typeof targets = [];
    try {
      // ponytail: 순차 요청, 수천 건 단위면 bulk API 추가 필요
      for (const row of targets) {
        await editImageLabel(Number(id), row.path, newLabel);
        done.push(row);
      }
      toast.success(`${newLabel}: ${done.length}${t("SheetCountUnit")}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      // 실패해도 성공한 건까지는 화면에 반영
      const doneSeqs = new Set(done.map((r) => r.seq));
      setProjectImagesInfo((prev) => prev.map((r) => (doneSeqs.has(r.seq) ? { ...r, label: newLabel } : r)));
      setProjectCountInfo((prev) => {
        if (!prev?.label_count) return prev;
        const counts = { ...prev.label_count };
        done.forEach((r) => {
          const prevLabel = r.label as string;
          if (prevLabel) counts[prevLabel] = Math.max((counts[prevLabel] ?? 0) - 1, 0);
          else counts.total = (counts.total ?? 0) + 1;
          counts[newLabel] = (counts[newLabel] ?? 0) + 1;
        });
        return { ...prev, label_count: counts };
      });
      setMultiSeqs([]);
    }
  };

  const handleLabelClick = useCallback(
    async (e: any = undefined) => {
      const start = new Date();
      if (id && multiSeqs.length) {
        await handleBulkLabel(className);
      } else if (id) {
        const previousLabel = selectedRow.label as string;
        const newLabel = className;

        try {
          if (previousLabel !== newLabel) {
            await editImageLabel(Number(id), selectedRow.path!, newLabel);

            setProjectCountInfo((prev) => {
              if (!prev?.label_count) return prev;

              const updatedCounts = { ...prev.label_count };

              if (previousLabel) {
                updatedCounts[previousLabel] = Math.max((updatedCounts[previousLabel] ?? 0) - 1, 0);
              } else {
                updatedCounts["total"] = (updatedCounts["total"] ?? 0) + 1;
              }
              updatedCounts[newLabel] = (updatedCounts[newLabel] ?? 0) + 1;

              return {
                ...prev,
                label_count: updatedCounts,
              };
            });

            setProjectImagesInfo((prevImages: any) => {
              const updatedImages = prevImages.map((image: any) =>
                image.seq === selectedRow.seq ? { ...image, label: newLabel } : image,
              );
              return updatedImages;
            });
          }

          const currentIndex = projectImagesInfo.findIndex((image) => image.seq === selectedRow.seq);
          const nextIndex = currentIndex + 1;

          if (nextIndex >= projectImagesInfo.length) return;

          setSelectedRow(projectImagesInfo[nextIndex]);
        } catch (error) {
          console.error(error);
        }
      }

      if (e) {
        e.target.blur();
      }

      const end = new Date();
      const elapsedTime = end.getTime() - start.getTime();
    },
    [id, selectedRow, projectImagesInfo, projectCountInfo, multiSeqs],
  );

  useEffect(() => {
    if (!registerClick) return;
    registerClick(handleLabelClick);
    // 언마운트 시 해제: 남아있으면 검사 프로젝트 등 버튼 없는 화면에서도 숫자키로 이전 프로젝트 라벨링이 실행됨
    return () => registerClick(null);
  }, [handleLabelClick, registerClick]);

  return (
    <button
      className={`label-button ${className}`}
      onClick={handleLabelClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        height: 28,
        padding: "0 8px 0 12px",
        borderRadius: 14,
        border: "1px solid var(--stroke)",
        background: "var(--dk-surface, #fff)",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        cursor: "pointer",
        transition: "background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "#f5f5f7";
        e.currentTarget.style.borderColor = "rgba(0,0,0,0.12)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "#fff";
        e.currentTarget.style.borderColor = "var(--stroke)";
      }}
    >
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: "var(--icon-deactivate)",
          whiteSpace: "nowrap",
          fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', system-ui, sans-serif",
        }}
      >
        {className}
      </span>

      <span
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 18,
          height: 18,
          padding: "0 5px",
          borderRadius: 9,
          backgroundColor: "var(--stroke)",
          fontSize: 11,
          fontWeight: 700,
          color: "var(--icon-deactivate)",
          flexShrink: 0,
        }}
      >
        {configNumber}
      </span>
    </button>
  );
}
